import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { SiteHeader, Sidebar } from './components/Shell';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { SubmitWizardPage } from './pages/SubmitWizardPage';
import { RequestDetailPage } from './pages/RequestDetailPage';
import { RegistryPage } from './pages/RegistryPage';
import { ReviewQueuePage } from './pages/ReviewQueuePage';
import { AdminPage } from './pages/AdminPage';
import type { Role } from '@air/shared';

function RequireAuth({ roles }: Readonly<{ roles?: Role[] }>) {
  const { user, status, effectiveRole } = useAuth();
  if (status === 'loading') {
    return (
      <div style={{ padding: 40 }} className="muted">
        Loading…
      </div>
    );
  }
  if (status === 'anonymous' || !user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  // Route guards use effectiveRole so an admin previewing-as-employee sees
  // exactly what an employee would. The admin can flip back via the header.
  if (roles && effectiveRole && !roles.includes(effectiveRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function AuthedShell() {
  return (
    <div className="app">
      <SiteHeader />
      <div className="layout">
        <Sidebar />
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AuthedShell />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/requests/new" element={<SubmitWizardPage />} />
          <Route path="/requests/:id" element={<RequestDetailPage />} />
          <Route path="/registry" element={<RegistryPage />} />
        </Route>
      </Route>

      <Route element={<RequireAuth roles={['it_reviewer', 'admin']} />}>
        <Route element={<AuthedShell />}>
          <Route path="/reviews/it" element={<ReviewQueuePage role="it" />} />
        </Route>
      </Route>

      <Route element={<RequireAuth roles={['legal_reviewer', 'admin']} />}>
        <Route element={<AuthedShell />}>
          <Route path="/reviews/legal" element={<ReviewQueuePage role="legal" />} />
        </Route>
      </Route>

      <Route element={<RequireAuth roles={['admin']} />}>
        <Route element={<AuthedShell />}>
          <Route path="/admin" element={<AdminPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
